import React from 'react';
import { Box, Button, FormControl, FormHelperText, Grid, MenuItem, Select, TextField, Typography } from '@mui/material';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { Icon } from "@iconify/react";
import { mergeFinancialSupportWizard, readFinancialSupportWizard } from './financialSupportProgramWizardStorage';
import {
  compactFormFieldSx,
  createMenuProps,
  FieldLabel,
  helperTextSx,
  placeholderTextSx,
  sectionTitleSx,
} from '../../styles/formStyles';
import { primaryIconColor } from '../../styles/iconStyles';
import {
  FinancialSupportWizardSidebar,
  wizardContentCardSx,
  wizardContentGridSx,
  wizardPageShellSx,
  wizardPageTitleSx,
  wizardPrimaryButtonSx,
  wizardSidebarGridSx,
  wizardTextActionButtonSx,
} from '../../components/financialSupportWizard';

const rangeFieldSx = {
  position: 'relative',
  border: '1px solid #E4E6F1',
  borderRadius: '8px',
  backgroundColor: '#FFFFFF',
  minHeight: 46,
  pr: 6,
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 44px minmax(0, 1fr)',
  alignItems: 'center',
  columnGap: 0,
  transition: 'border-color 160ms ease, box-shadow 160ms ease',
  '&:hover': {
    borderColor: '#E4E6F1',
  },
  '&:focus-within': {
    borderColor: '#294190',
    boxShadow: '0 0 0 1px #294190 inset',
  },
};

const rangeInputSx = {
  border: 'none',
  outline: 'none',
  backgroundColor: 'transparent',
  width: '100%',
  minWidth: 0,
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'textfield',
  fontSize: 16,
  lineHeight: '24px',
  letterSpacing: '0.5px',
  color: '#1C1B1F',
  fontWeight: 400,
  fontFamily: 'Roboto, sans-serif',
  '&::-webkit-calendar-picker-indicator': {
    opacity: 0,
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    margin: 0,
    padding: 0,
    cursor: 'pointer',
  },
  '&::-webkit-inner-spin-button': {
    display: 'none',
  },
  '&::-webkit-clear-button': {
    display: 'none',
  },
};

const rangeInputWrapSx = {
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  minHeight: 46,
  px: 2,
};

const fieldSx = compactFormFieldSx;
const menuProps = createMenuProps({ sx: { width: 'auto' } });

const financialProgramSchema = z.object({
  programName: z.string().min(1, { message: 'Program Name is required' }),
  supportType: z.string().min(1, { message: 'Support Type is required' }),
  serviceProvider: z.string().min(1, { message: 'Service Provider is required' }),
  region: z.string().min(1, { message: 'Region is required' }),
  currency: z.string().min(1, { message: 'Currency is required' }),
  validityStart: z.string().min(1, { message: 'Validity Start is required' }),
  validityEnd: z.string().min(1, { message: 'Validity End is required' }),
  description: z.string().optional(),
}).refine((data) => {
  if (!data.validityStart || !data.validityEnd) {
    return true;
  }

  return new Date(data.validityEnd) >= new Date(data.validityStart);
}, {
  message: 'Validity End must be on or after Validity Start',
  path: ['validityEnd'],
});

const stepItems = [
  { id: 1, label: 'Basic Info', active: true, completed: true },
  { id: 2, label: 'Contribution Rules' },
  { id: 3, label: 'Plans' },
  { id: 4, label: 'Review & Publish' },
];

export default function CreateFinancialSupportProgram() {
  const navigate = useNavigate();
  const wizardData = readFinancialSupportWizard();
  const savedBasicInfo = wizardData.basicInfo || {};
  const { handleSubmit, control, watch, formState: { errors } } = useForm({
    resolver: zodResolver(financialProgramSchema),
    defaultValues: {
      programName: savedBasicInfo.programName || '',
      supportType: savedBasicInfo.supportType || '',
      serviceProvider: savedBasicInfo.serviceProvider || '',
      region: savedBasicInfo.region || '',
      currency: savedBasicInfo.currency || '',
      validityStart: savedBasicInfo.validityStart || '',
      validityEnd: savedBasicInfo.validityEnd || '',
      description: savedBasicInfo.description || '',
    },
  });

  const supportTypeOptions = [
    { value: 'CREDIT', label: 'Credit' },
    { value: 'GRANT_SUBSIDY', label: 'Grant/Subsidy' },
    { value: 'INVESTMENT', label: 'Investment' },
  ];
  const serviceProviderOptions = ['National Bank', 'Rural Cooperative', 'Development Fund', 'Government Agency'];
  const regionOptions = ['India', 'Kenya', 'Nigeria', 'Bangladesh'];
  const currencyOptions = ['INR', 'KES', 'NGN', 'USD'];
  const validityStart = watch('validityStart');
  const validityEnd = watch('validityEnd');

  const onSubmit = (data) => {
    mergeFinancialSupportWizard({
      basicInfo: data,
    });
    navigate('/financialSupport/create-program/contribution-rules');
  };

  return (
    <Box sx={wizardPageShellSx}>
      <Typography className="app-page-title" sx={{ ...wizardPageTitleSx, mb: 4.5 }}>
        Create Financial Support Program
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)} className="material-form">
        <Grid container spacing={3} alignItems="stretch">
          <Grid size={{ xs: 12, md: 3.2, lg: 3 }} sx={wizardSidebarGridSx}>
            <FinancialSupportWizardSidebar steps={stepItems} />
          </Grid>

          <Grid size={{ xs: 12, md: 8.8, lg: 9 }} sx={wizardContentGridSx}>
            <Box sx={wizardContentCardSx}>
              <Typography sx={{ ...sectionTitleSx, mb: 4 }}>
                Basic Info
              </Typography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FieldLabel label="Program Name" required />
                  <Controller
                    name="programName"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        placeholder="Enter program name"
                        fullWidth
                        error={!!error}
                        helperText={error ? error.message : ''}
                        sx={{ ...fieldSx, ...helperTextSx }}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FieldLabel label="Support Type" required />
                  <FormControl fullWidth error={!!errors.supportType} sx={helperTextSx}>
                    <Controller
                      name="supportType"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          displayEmpty
                          MenuProps={menuProps}
                          IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />}
                          sx={fieldSx}
                          renderValue={(selected) => !selected ? <Box sx={placeholderTextSx}>Select support type</Box> : selected}
                        >
                          {supportTypeOptions.map((item) => (
                            <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {!!errors.supportType && <FormHelperText error>{errors.supportType.message}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FieldLabel label="Service Provider" required />
                  <FormControl fullWidth error={!!errors.serviceProvider} sx={helperTextSx}>
                    <Controller
                      name="serviceProvider"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          displayEmpty
                          MenuProps={menuProps}
                          IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />}
                          sx={fieldSx}
                          renderValue={(selected) => !selected ? <Box sx={placeholderTextSx}>Select service provider</Box> : selected}
                        >
                          {serviceProviderOptions.map((item) => (
                            <MenuItem key={item} value={item}>{item}</MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {!!errors.serviceProvider && <FormHelperText error>{errors.serviceProvider.message}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FieldLabel label="Regions" required />
                  <FormControl fullWidth error={!!errors.region} sx={helperTextSx}>
                    <Controller
                      name="region"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          displayEmpty
                          MenuProps={menuProps}
                          IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />}
                          sx={fieldSx}
                          renderValue={(selected) => !selected ? <Box sx={placeholderTextSx}>Select regions where program will be available</Box> : selected}
                        >
                          {regionOptions.map((item) => (
                            <MenuItem key={item} value={item}>{item}</MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {!!errors.region && <FormHelperText error>{errors.region.message}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FieldLabel label="Currency" required />
                  <FormControl fullWidth error={!!errors.currency} sx={helperTextSx}>
                    <Controller
                      name="currency"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          displayEmpty
                          MenuProps={menuProps}
                          IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />}
                          sx={fieldSx}
                          renderValue={(selected) => !selected ? <Box sx={placeholderTextSx}>Select currency</Box> : selected}
                        >
                          {currencyOptions.map((item) => (
                            <MenuItem key={item} value={item}>{item}</MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {!!errors.currency && <FormHelperText error>{errors.currency.message}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FieldLabel label="Validity" required />
                  <Box sx={{ ...rangeFieldSx, gridTemplateColumns: { xs: 'minmax(0, 1fr) 36px minmax(0, 1fr)', sm: 'minmax(0, 1fr) 44px minmax(0, 1fr)' }, pr: { xs: 5.5, sm: 6 } }}>
                    <Box sx={rangeInputWrapSx}>
                      <Controller
                        name="validityStart"
                        control={control}
                        render={({ field }) => (
                          <Box
                            component="input"
                            type="date"
                            {...field}
                            sx={rangeInputSx}
                            max={validityEnd || undefined}
                          />
                        )}
                      />
                    </Box>
                    <Typography sx={{ px: 1.5, fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#625B71', textAlign: 'center' }}>
                      to
                    </Typography>
                    <Box sx={rangeInputWrapSx}>
                      <Controller
                        name="validityEnd"
                        control={control}
                        render={({ field }) => (
                          <Box
                            component="input"
                            type="date"
                            {...field}
                            sx={rangeInputSx}
                            min={validityStart || undefined}
                          />
                        )}
                      />
                    </Box>
                    <Box
                      component="span"
                      className="material-symbols-rounded"
                      sx={{
                        position: 'absolute',
                        right: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: 20,
                        color: '#1C1B1F',
                        pointerEvents: 'none',
                      }}
                    >
                      calendar_month
                    </Box>
                  </Box>
                  <FormHelperText error sx={{ ml: 0, mt: 1, fontSize: 12, lineHeight: '16px', letterSpacing: '0.4px' }}>
                    {errors.validityStart?.message || errors.validityEnd?.message || ''}
                  </FormHelperText>
                </Grid>

                <Grid size={12}>
                  <FieldLabel label="Description" />
                  <Controller
                    name="description"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        placeholder="Describe the purpose and benefits..."
                        fullWidth
                        multiline
                        minRows={4}
                        error={!!error}
                        helperText={error ? error.message : ''}
                        sx={{ ...fieldSx, ...helperTextSx }}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 2, mt: 7 }}>
                <Button
                  onClick={() => navigate('/financialSupport')}
                  sx={wizardTextActionButtonSx}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  sx={{ ...wizardPrimaryButtonSx, minWidth: { sm: 160 } }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}
